
import { Component, Vue, Watch } from 'vue-property-decorator';
import Themeable from 'vuetify/lib/mixins/themeable';
import { DataTableHeader } from 'vuetify';
import ChatRepositoryInterface, {
  ChatInterface,
  chatRateMap,
  ChatsSearchResultInterface,
  ChatsWSMessageInterface,
} from '@/services/QA/chat/interfaces';

@Component({
  mixins: [
    Themeable,
  ],
  components: {},
  inject: [
    'chatRepository',
  ],
  sockets: {
    async chatsData(msg: ChatsWSMessageInterface): Promise<void> {
      this.$emit('updateChats', msg.chats);
    },
    searchResultData(msg: ChatsSearchResultInterface): void {
      this.$emit('searchResult', msg.chats, msg.totalItems);
    },
  },
})
export default class ChatIndex extends Vue {
  private loading: boolean = true;
  private chats: ChatInterface[] = [];
  private search: string = '';
  private chatRateMap: object = chatRateMap;
  private totalItems: number = 0;
  private currentPage: number = 1;
  private itemsPerPage: number = 10;
  private partialChatsData: Array<Partial<ChatInterface>> = [];
  private responseReceived: boolean = false;

  private agentId: string | undefined = undefined;

  private headers: DataTableHeader[] = [
    { text: 'ID', value: 'id' },
    { text: 'External', value: 'external', sortable: false },
    { text: 'Date & Time', value: 'endedTimestamp' },
    { text: 'Chat Rated', value: 'rate', sortable: false },
    { text: 'Client Name', value: 'visitor.name', sortable: false },
    { text: 'Client Logged', value: 'visitor.isLogged', sortable: false },
    { text: 'Participants', value: 'agents', sortable: false },
  ];

  private sortBy: string = 'endedTimestamp';
  private sortDesc: boolean = true;
  private options: { sortBy: string[], sortDesc: boolean[] } = { sortBy: [this.sortBy], sortDesc: [this.sortDesc] };

  private readonly chatRepository!: ChatRepositoryInterface;

  public beforeCreate(): void {
    this.$on('updateChats', (chats: ChatInterface[]) => {
      this.mergeChatMessageData(chats);
    });

    this.$on('searchResult', (chats: ChatInterface[], totalItems: number) => {
      this.totalItems = totalItems;
      this.mergeChatMessageData(chats);
      this.$forceUpdate();
    });
  }

  public async created(): Promise<void> {
    const tag = this.$route.params.tag;
    const agentId = this.$route.params.agentId;
    if (tag && agentId) {
      this.search = tag;
      this.agentId = agentId;
      await this.searchChats();
      return;
    }
    await this.initialize();
  }

  private async initialize(): Promise<void> {
    this.loading = true;
    this.responseReceived = false;
    const [chats, totalItems] = await this.chatRepository
      .getAllReviewedChats(this.currentPage, this.sortBy, this.sortDesc);
    this.partialChatsData = chats;
    this.totalItems = totalItems;
    this.responseReceived = true;
  }

  private mergeChatMessageData(chats: ChatInterface[]): void {
    if (!this.responseReceived) {
      setTimeout(() => {
        this.mergeChatMessageData(chats);
      }, 200);
    }

    if (!this.loading) {
      return;
    }

    this.chats = chats;
    this.partialChatsData.forEach((partialChatData) => {
      const chatIndex = this.chats.findIndex((chat) => chat.id === partialChatData.id);
      if (chatIndex !== -1) {
        this.chats[chatIndex].tags = partialChatData.tags || [];
        this.chats[chatIndex].reviewedOn = partialChatData.reviewedOn;
      }
    });

    this.loading = false;
  }

  private async changePage(): Promise<void> {
    this.loading = true;
    this.responseReceived = false;
    if (this.search !== '') {
      await this.chatRepository.searchChats(this.currentPage, this.search.trim(), this.sortBy, this.sortDesc);
      this.responseReceived = true;
    } else {
      this.agentId = undefined;
      const [chats, totalItems] = await this.chatRepository
        .getAllReviewedChats(this.currentPage, this.sortBy, this.sortDesc);
      this.partialChatsData = chats;
      this.totalItems = totalItems;
      this.responseReceived = true;
    }
  }

  private async searchChats(): Promise<void> {
    this.loading = true;
    this.responseReceived = false;
    this.currentPage = 1;
    if (this.search.trim() === '') {
      this.agentId = undefined;
      await this.initialize();
      return;
    }

    await this.chatRepository.searchChats(this.currentPage, this.search.trim(), this.sortBy, this.sortDesc);
    this.responseReceived = true;
  }

  @Watch('options')
  private async watchOptions(): Promise<void> {
    if (this.loading) {
      return;
    }

    const sortBy = this.options.sortBy[0];
    const sortDesc = this.options.sortDesc[0];

    if (!sortBy && !sortDesc) {
      this.sortBy = 'endedTimestamp';
      this.sortDesc = true;
    } else {
      this.sortBy = sortBy;
      this.sortDesc = sortDesc;
    }

    if (this.search.trim() !== '') {
      this.loading = true;
      this.responseReceived = false;
      await this.chatRepository.searchChats(this.currentPage, this.search.trim(), this.sortBy, this.sortDesc);
      this.responseReceived = true;
      return;
    }
    await this.initialize();
  }
}
